import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import OpportunityContact from "@/services/opportunity/opportunity-contact";
import type { GetOpportunityContact } from "@/dtos/OpportunityContact.dto";

export interface OpportunityContactsParams {
  opportunityId: string;
  organizationId: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useOpportunityContacts(
  params: Ref<OpportunityContactsParams | null> | OpportunityContactsParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["opportunityContacts", "disabled"];
    return ["opportunityContacts", p.opportunityId, p.organizationId];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.opportunityId && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetOpportunityContact = {
        opportunityId: p.opportunityId,
        organizationId: p.organizationId,
      };

      const result = await OpportunityContact.getOpportunityContacts(queryParams);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load opportunity contacts");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
